import React from 'react';
import { Button, Section, SectionHeader } from '../../design-system';
import MarketingLayout from '../layouts/MarketingLayout';
import FadeIn from '../components/FadeIn';
import AdvertiserInquiryForm from '../../Pages/Landing/AdvertiserInquiryForm';
import advertiserImage from '../../assets/images/cover2.png';

const FORMATS = [
  {
    title: 'Video Advertising',
    items: ['15-, 30-, 45- and 60-second spots', 'Full-screen immersive (1920 × 1080)', 'MP4 format', 'Non-skippable'],
  },
  {
    title: 'Display Advertising',
    items: ['Full-screen static and animated ads', 'Customizable duration', 'Cost-effective reach', 'HD quality delivery'],
  },
];

const BUYING_OPTIONS = [
  {
    title: 'Direct with INVERT',
    description: 'Work with our team to plan campaigns, select screens, and launch with hands-on support.',
  },
  {
    title: 'Self-Serve Dashboard',
    description: 'Create an advertiser account, upload creatives, and manage campaigns from your dashboard.',
  },
  {
    title: 'Launch Flow',
    description: 'Start with a single screen and go live quickly with our streamlined launch experience.',
  },
];

export default function Advertise() {
  return (
    <MarketingLayout navDark={false}>
      <section className="bg-white pt-32 lg:pt-40 pb-20 lg:pb-28 border-b border-invert-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <p className="text-xs font-semibold uppercase tracking-widest text-invert-orange mb-6">
                For Advertisers
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-5xl font-bold tracking-tight text-invert-navy mb-6">
                Reach audiences on intelligent screens
              </h1>
              <p className="text-xl text-slate-600 mb-4">
                INVERT screens aren&apos;t just digital signage — they&apos;re AI-powered business displays in premium local venues.
              </p>
              <p className="text-slate-500 mb-8">
                Reach customers during natural content breaks at restaurants, bars, gyms, retail stores, and high-traffic locations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button to="/advertiserSignup" size="lg">Start Advertising</Button>
                <Button to="/network" variant="outline" size="lg">Browse Network</Button>
              </div>
              <p className="text-sm text-slate-500 mt-6">
                <Button to="/proof" variant="ghost" size="sm" className="!p-0 !h-auto text-invert-orange hover:text-invert-orange-deep">
                  See who&apos;s already on the network →
                </Button>
              </p>
            </FadeIn>
            <FadeIn delay={0.15}>
              <img
                src={advertiserImage}
                alt="Advertiser campaign on INVERT network"
                className="rounded-2xl shadow-xl"
              />
            </FadeIn>
          </div>
        </div>
      </section>

      <Section>
        <SectionHeader
          label="Buying Options"
          title="Three ways to launch"
          subtitle="Choose the path that fits your campaign goals."
        />
        <div className="grid md:grid-cols-3 gap-6">
          {BUYING_OPTIONS.map((opt, i) => (
            <FadeIn key={opt.title} delay={i * 0.1}>
              <div className="rounded-2xl border border-invert-border p-6 h-full hover:shadow-md transition-shadow">
                <h3 className="text-lg font-semibold text-invert-navy mb-3">{opt.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{opt.description}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      <Section className="bg-slate-50">
        <SectionHeader
          label="Advertising Formats"
          title="Premium placements, proven formats"
        />
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {FORMATS.map((fmt) => (
            <FadeIn key={fmt.title}>
              <div className="bg-white rounded-2xl p-6 border border-invert-border">
                <h3 className="font-semibold text-invert-navy mb-4">{fmt.title}</h3>
                <ul className="space-y-2">
                  {fmt.items.map((item) => (
                    <li key={item} className="text-sm text-slate-600 flex items-center gap-2">
                      <span className="text-invert-orange">•</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      <Section id="advertiser-inquiry">
        <SectionHeader
          label="Get Started"
          title="Start advertising on INVERT"
          subtitle="Tell us about your campaign and we'll help you reach local audiences."
        />
        <FadeIn className="max-w-2xl mx-auto">
          <AdvertiserInquiryForm source="advertise" />
        </FadeIn>
      </Section>
    </MarketingLayout>
  );
}
